import { decrypt, encrypt } from './symmetric';
import { secureRandom, zeroMemory } from './utils';

export interface RecoveryVaultKey {
  id: string;
  key: Uint8Array;
}

export interface RecoveryPayload {
  privateKeys: Uint8Array;
  vaultKeys: RecoveryVaultKey[];
}

export interface EncryptedRecoveryPayload {
  ciphertext: Uint8Array;
  nonce: Uint8Array;
}

const toBase64 = (bytes: Uint8Array) => {
  let value = '';
  bytes.forEach((byte) => { value += String.fromCharCode(byte); });
  return btoa(value);
};

const fromBase64 = (value: string) => Uint8Array.from(atob(value), (char) => char.charCodeAt(0));

/** Generates the 256-bit secret that must be split with Shamir client-side. */
export function generateRecoveryKey(): Uint8Array {
  return secureRandom(32);
}

/** Encrypts private key material and vault keys as a single authenticated blob. */
export function encryptRecoveryPayload(
  payload: RecoveryPayload,
  recoveryKey: Uint8Array,
  aad: Uint8Array = new Uint8Array(),
): EncryptedRecoveryPayload {
  if (recoveryKey.length !== 32) throw new Error('Recovery key must be 32 bytes');
  const serialized = new TextEncoder().encode(JSON.stringify({
    version: 1,
    privateKeys: toBase64(payload.privateKeys),
    vaultKeys: payload.vaultKeys.map(({ id, key }) => ({ id, key: toBase64(key) })),
  }));
  try {
    return encrypt(serialized, recoveryKey, aad);
  } finally {
    zeroMemory(serialized);
  }
}

/** Decrypts a recovery blob only after the Shamir secret has been reconstructed. */
export function decryptRecoveryPayload(
  encrypted: EncryptedRecoveryPayload,
  recoveryKey: Uint8Array,
  aad: Uint8Array = new Uint8Array(),
): RecoveryPayload {
  const plaintext = decrypt(encrypted.ciphertext, encrypted.nonce, recoveryKey, aad);
  try {
    const value = JSON.parse(new TextDecoder().decode(plaintext));
    if (value.version !== 1 || typeof value.privateKeys !== 'string' || !Array.isArray(value.vaultKeys)) {
      throw new Error('Unsupported recovery payload');
    }
    return {
      privateKeys: fromBase64(value.privateKeys),
      vaultKeys: value.vaultKeys.map((entry: { id: unknown; key: unknown }) => {
        if (typeof entry.id !== 'string' || typeof entry.key !== 'string') throw new Error('Invalid recovery vault key');
        return { id: entry.id, key: fromBase64(entry.key) };
      }),
    };
  } finally {
    zeroMemory(plaintext);
  }
}
